'use client';

import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const codeLines = [
    { text: 'function calculatePrice(items: Item[]) {', highlight: false, indent: 0 },
    { text: '  const base = items.reduce((s, i) => s + i.price, 0);', highlight: false, indent: 1 },
    { text: '  const tax = applyTaxLogic(base);', highlight: true, indent: 1 },
    { text: '  const discount = getDiscount(base);', highlight: true, indent: 1 },
    { text: '  const converted = convertCurrency(tax - discount);', highlight: true, indent: 1 },
    { text: '  return converted;', highlight: false, indent: 1 },
    { text: '}', highlight: false, indent: 0 },
];

const timelineEntries = [
    { time: '2:41 PM', label: 'v3 — current', active: false, color: 'text-muted-foreground' },
    { time: '1:18 PM', label: 'v2 — tax added', active: true, color: 'text-primary' },
    { time: '11:02 AM', label: 'v1 — initial', active: false, color: 'text-muted-foreground' },
];

export default function HeroSection() {
    const heroRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const el = heroRef.current;
        if (!el) return;
        const onMouseMove = (e: MouseEvent) => {
            const rect = el.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
            const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
            const blob1 = el.querySelector<HTMLElement>('.hero-blob-1');
            const blob2 = el.querySelector<HTMLElement>('.hero-blob-2');
            if (blob1) {
                blob1.style.transform = `translate(${x * 0.6}px, ${y * 0.6}px)`;
            }
            if (blob2) {
                blob2.style.transform = `translate(${-x * 0.4}px, ${-y * 0.4}px)`;
            }
        };
        el.addEventListener('mousemove', onMouseMove);
        return () => el.removeEventListener('mousemove', onMouseMove);
    }, []);

    return (
        <section
            ref={heroRef}
            className="relative min-h-screen flex flex-col items-center justify-center pt-32 pb-16 px-6 overflow-hidden wireframe-bg"
        >
            {/* Atmospheric blobs */}
            <div
                className="hero-blob-1 absolute top-1/4 left-1/4 w-[600px] h-[600px] blob-green animate-pulse-glow pointer-events-none"
                style={{ transition: 'transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94)' }}
            />
            <div
                className="hero-blob-2 absolute bottom-1/4 right-1/4 w-[500px] h-[500px] blob-cyan animate-pulse-glow pointer-events-none"
                style={{ transition: 'transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94)', animationDelay: '1.5s' }}
            />

            <div className="relative z-10 max-w-7xl mx-auto w-full">
                {/* Eyebrow */}
                <div className="flex justify-center mb-8">
                    <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full glass-panel border border-border">
                        <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
                        <span className="section-label">Block-Level Version Control // 2026</span>
                    </div>
                </div>

                {/* Headline */}
                <h1 className="text-hero font-extrabold text-center text-foreground max-w-5xl mx-auto mb-6">
                    Version control,{' '}
                    <span className="text-gradient-primary italic font-light">down to the code.</span>
                </h1>

                {/* Subheadline */}
                <p className="text-center text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed font-medium">
                    Restore individual blocks of code without reverting entire files.{' '}
                    <span className="text-foreground">CapState</span> gives developers granular control over their code history.
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
                    <a href="https://marketplace.visualstudio.com/items?itemName=CodigoFoundry.capstate-latest&ssr=false#review-details" target="_blank" className="btn-primary">
                        Get Started Free
                        <Icon name="ArrowRightIcon" size={16} />
                    </a>
                    <button
                        className="btn-secondary"
                        onClick={() => {
                            document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                    >
                        <Icon name="PlayIcon" size={16} className="text-accent" />
                        See How It Works
                    </button>
                </div>

                {/* Hero Visual: Code Editor + Timeline */}
                <div className="relative max-w-5xl mx-auto">
                    {/* Floating stat cards */}
                    <div
                        className="absolute -top-8 -left-4 md:-left-12 glass-panel-light rounded-2xl px-4 py-3 border border-border z-20 animate-float-up hidden sm:block"
                        style={{ animationDelay: '0.5s' }}
                    >
                        <p className="section-label mb-0.5">Blocks Tracked</p>
                        <p className="text-2xl font-bold text-foreground">2.4M+</p>
                    </div>
                    <div
                        className="absolute -top-4 -right-4 md:-right-12 glass-panel-light rounded-2xl px-4 py-3 border border-border z-20 animate-float-down hidden sm:block"
                        style={{ animationDelay: '1s' }}
                    >
                        <p className="section-label mb-0.5">Avg Recovery</p>
                        <p className="text-2xl font-bold text-foreground">
                            <span className="text-gradient-accent">8s</span>
                        </p>
                    </div>
                    <div
                        className="absolute -bottom-6 left-1/2 -translate-x-1/2 glass-panel-light rounded-2xl px-4 py-3 border border-border z-20 animate-float-up hidden sm:block"
                        style={{ animationDelay: '0.2s' }}
                    >
                        <p className="section-label mb-0.5">Developers</p>
                        <p className="text-2xl font-bold text-foreground">
                            <span className="text-gradient-primary">47k</span>
                        </p>
                    </div>

                    {/* Editor Card */}
                    <div className="glass-panel rounded-2xl border border-border overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.5)] glow-primary">
                        {/* Editor Title Bar */}
                        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-card">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500 opacity-70" />
                                <div className="w-3 h-3 rounded-full bg-yellow-500 opacity-70" />
                                <div className="w-3 h-3 rounded-full bg-green-500 opacity-70" />
                            </div>
                            <span className="font-mono-code text-xs text-muted-foreground">pricing.ts</span>
                            <div className="flex items-center gap-2">
                                <span className="section-label text-primary">● CapState active</span>
                            </div>
                        </div>

                        {/* Editor Body */}
                        <div className="flex">
                            {/* Code Area */}
                            <div className="flex-1 p-5 overflow-x-auto">
                                <div className="code-block space-y-0.5 min-w-max">
                                    {codeLines.map((line, i) => (
                                        <div
                                            key={i}
                                            className={`flex items-center gap-3 px-2 py-0.5 rounded-sm ${line.highlight ? 'code-line-highlight' : ''
                                                }`}
                                        >
                                            <span className="text-muted-foreground w-5 text-right shrink-0 text-xs select-none">
                                                {i + 1}
                                            </span>
                                            <span
                                                className={`${line.highlight ? 'text-primary' : 'text-foreground'
                                                    } opacity-90`}
                                                style={{ paddingLeft: `${line.indent * 12}px` }}
                                            >
                                                {line.text}
                                                {i === 0 && (
                                                    <span className="animate-cursor-blink text-accent ml-0.5">|</span>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Timeline Sidebar */}
                            <div className="w-52 border-l border-border p-4 shrink-0 hidden sm:block">
                                <p className="section-label mb-4">Block History</p>
                                <div className="relative">
                                    {/* Vertical line */}
                                    <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />

                                    <div className="space-y-5">
                                        {timelineEntries.map((entry, i) => (
                                            <div key={i} className="flex items-start gap-3 relative">
                                                <div
                                                    className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 mt-0.5 z-10 ${entry.active
                                                        ? 'border-primary bg-primary shadow-[0_0_8px_rgba(232,255,71,0.6)]'
                                                        : 'border-muted bg-muted'
                                                        }`}
                                                />
                                                <div>
                                                    <p
                                                        className={`font-mono-code text-xs font-medium ${entry.active ? 'text-primary' : 'text-muted-foreground'
                                                            }`}
                                                    >
                                                        {entry.time}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-0.5">{entry.label}</p>
                                                    {entry.active && (
                                                        <button className="mt-1.5 text-xs font-semibold text-accent hover:text-accent/80 transition-colors flex items-center gap-1">
                                                            Restore
                                                            <Icon name="ArrowUturnLeftIcon" size={10} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Editor Status Bar */}
                        <div className="flex items-center justify-between px-5 py-2 border-t border-border bg-card">
                            <div className="flex items-center gap-4">
                                <span className="font-mono-code text-xs text-muted-foreground">TypeScript</span>
                                <span className="font-mono-code text-xs text-muted-foreground">UTF-8</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                                <span className="font-mono-code text-xs text-primary">3 blocks tracked</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}